import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import { inRange } from "lodash";

import {
  INTEGRATION_FIELD_NAMES,
  IntegrationErrors
} from "../../../../../../Pages/Integrations/types";
import { INTEGRATIONS_INPUT_LENGTH } from "../../../../../../shared";

const useIntegrationsInputs = (
  errors: IntegrationErrors,
  optionalPassword: boolean | undefined,
  setErrors: Dispatch<SetStateAction<IntegrationErrors>>
) => {
  const { t } = useTranslation();

  const isNotEmpty = (value: string, field: INTEGRATION_FIELD_NAMES) => {
    const fieldTranslation = t(`Dialog##${field}`);

    if (field === INTEGRATION_FIELD_NAMES.Password && optionalPassword) {
      return "";
    }

    return value?.trim()
      ? ""
      : t(`Dialog##field cannot be empty`, { fieldName: fieldTranslation });
  };

  const maxLength = (value: string, limit: number, msg: any) =>
    value?.trim().length > limit ? msg : "";

  const portValidation = (portNum: number, msg: any) => {
    const isBetween = inRange(
      portNum,
      INTEGRATIONS_INPUT_LENGTH.PortMin,
      INTEGRATIONS_INPUT_LENGTH.PortMax + 1
    );
    return isBetween ? "" : msg;
  };

  const validateField = (
    field: INTEGRATION_FIELD_NAMES,
    value: any
  ): string => {
    switch (field) {
      case INTEGRATION_FIELD_NAMES.Name:
        return (
          isNotEmpty(value, field) ||
          maxLength(
            value,
            INTEGRATIONS_INPUT_LENGTH.Name,
            t("Dialog##name not valid", {
              maxNumber: INTEGRATIONS_INPUT_LENGTH.Name
            })
          )
        );

      case INTEGRATION_FIELD_NAMES.Host:
        return (
          isNotEmpty(value, field) ||
          maxLength(
            value,
            INTEGRATIONS_INPUT_LENGTH.Host,
            t("Dialog##host cannot contain more than", {
              maxNumber: INTEGRATIONS_INPUT_LENGTH.Host
            })
          )
        );

      case INTEGRATION_FIELD_NAMES.Port:
        return (
          isNotEmpty(value, field) ||
          portValidation(
            value,
            t("Dialog##port must be between", {
              minNumber: INTEGRATIONS_INPUT_LENGTH.PortMin,
              maxNumber: INTEGRATIONS_INPUT_LENGTH.PortMax
            })
          )
        );

      case INTEGRATION_FIELD_NAMES.RemotePath:
        return maxLength(
          value,
          INTEGRATIONS_INPUT_LENGTH.InputMaxLength,
          t("Dialog##remote directory path cannot contain more than", {
            maxNumber: INTEGRATIONS_INPUT_LENGTH.InputMaxLength
          })
        );

      case INTEGRATION_FIELD_NAMES.Username:
        return (
          isNotEmpty(value, field) ||
          maxLength(
            value,
            INTEGRATIONS_INPUT_LENGTH.Username,
            t("Dialog##username cannot contain more than", {
              maxNumber: INTEGRATIONS_INPUT_LENGTH.Username
            })
          )
        );

      case INTEGRATION_FIELD_NAMES.Password:
        return (
          isNotEmpty(value, field) ||
          maxLength(
            value,
            INTEGRATIONS_INPUT_LENGTH.InputMaxLength,
            t("Dialog##password cannot contain more than", {
              maxNumber: INTEGRATIONS_INPUT_LENGTH.InputMaxLength
            })
          )
        );

      default:
        return "";
    }
  };

  const handleBlur = (field: INTEGRATION_FIELD_NAMES, value: any) => {
    setErrors(prev => ({
      ...prev,
      [field]: validateField(field, value)
    }));
  };

  const clearFieldError = (field: keyof IntegrationErrors) => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return { handleBlur, clearFieldError };
};

export default useIntegrationsInputs;
